<template>
  <div class="page-outer">
    <!-- 固定頂部 header -->
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ isEdit ? '編輯受眾規則' : '新增受眾規則' }}</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" @click="handleBack">取消</button>
        <button type="button" class="btn btn-primary" :disabled="submitting" @click="handleSubmit">
          {{ submitting ? '儲存中...' : '儲存' }}
        </button>
      </div>
    </div>

    <div class="page-container">
    <div class="form-container">
      <form @submit.prevent="handleSubmit">
        <!-- 基本資訊 -->
        <div class="form-section">
          <h3 class="section-title">基本資訊</h3>
          
          <div class="form-group">
            <label class="required">規則名稱</label>
            <input
              ref="nameInput"
              v-model="formData.name"
              type="text"
              class="form-control"
              placeholder="請輸入規則名稱"
              maxlength="100"
              required
            />
          </div>

          <div class="form-group">
            <label>規則說明</label>
            <textarea
              v-model="formData.description"
              class="form-control"
              rows="3"
              placeholder="請輸入規則說明"
              :disabled="autoFillDescription"
            ></textarea>
            <div class="form-group-inline-option">
              <label class="checkbox-label">
                <input type="checkbox" v-model="autoFillDescription" />
                依設定規則自動填入說明
              </label>
            </div>
          </div>

          <div class="form-group">
            <label class="checkbox-label">
              <input type="checkbox" v-model="formData.isActive" />
              是否啟用
            </label>
          </div>
        </div>

        <!-- 條件設定 -->
        <div class="form-section">
          <h3 class="section-title">條件設定</h3>
          
          <MemberConditionGroups ref="conditionEditor" v-model="conditionGroups" mode="audience" />

          <div v-if="conditionSummary" class="condition-summary-box">
            <div class="summary-header">
              <i class="ri-lightbulb-line"></i>
              <span class="summary-title">條件效果說明</span>
            </div>
            <div class="summary-content">
              <p class="summary-text">{{ conditionSummary }}</p>
            </div>
          </div>
        </div>

        <!-- 動態更新設定 -->
        <div class="form-section">
          <h3 class="section-title">受眾更新設定</h3>
          
          <div class="form-group">
            <label class="checkbox-label">
              <input type="checkbox" v-model="formData.isDynamic" @change="handleDynamicChange" />
              啟用動態更新
            </label>
            <small class="form-text">
              <strong>不啟用：</strong>靜態受眾規則，建立時計算一次，之後需手動觸發更新<br>
              <strong>啟用：</strong>根據設定頻率自動更新受眾名單
            </small>
          </div>

          <div v-if="formData.isDynamic" class="form-group">
            <label class="required">更新頻率</label>
            <select v-model="formData.updateFrequency" class="form-control" required>
              <option value="">請選擇更新頻率</option>
              <option value="daily">每日</option>
              <option value="weekly">每週</option>
            </select>
            
            <!-- 更新頻率說明 -->
            <div v-if="formData.updateFrequency" class="frequency-description">
              <div v-if="formData.updateFrequency === 'daily'" class="description-box description-daily">
                <i class="ri-calendar-line"></i>
                <div class="description-content">
                  <strong>每日更新</strong>
                  <p>系統每天自動更新一次受眾名單，適合長期進行且對即時性要求不高的活動。</p>
                  <p class="info-text">更新時間：每天凌晨 02:00 執行</p>
                </div>
              </div>
              
              <div v-else-if="formData.updateFrequency === 'weekly'" class="description-box description-weekly">
                <i class="ri-calendar-2-line"></i>
                <div class="description-content">
                  <strong>每週更新</strong>
                  <p>系統每週自動更新一次受眾名單，適合週期性活動或名單變動頻率較低的場景。最節省系統資源。</p>
                  <p class="info-text">更新時間：每週一凌晨 03:00 執行</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 預覽 -->
        <div class="form-section">
          <h3 class="section-title">受眾預覽</h3>
          <div class="preview-section">
            <button type="button" class="btn btn-info" @click="handlePreview" :disabled="previewLoading">
              <i class="ri-eye-line"></i>
              {{ previewLoading ? '計算中...' : '預覽受眾人數' }}
            </button>
            <div v-if="previewResult" class="preview-result">
              <p><strong>預估受眾人數：</strong>{{ previewResult.estimatedCount }} 人</p>
              <p v-if="previewResult.executionTime"><strong>計算耗時：</strong>{{ previewResult.executionTime }} ms</p>
              <div v-if="previewResult.breakdown" class="breakdown">
                <h4>受眾分布</h4>
                <div v-if="previewResult.breakdown.byTier" class="breakdown-item">
                  <strong>會員等級：</strong>
                  <span v-for="(count, tier) in previewResult.breakdown.byTier" :key="tier" class="badge badge-info">
                    {{ tier }}: {{ count }}
                  </span>
                </div>
                <div v-if="previewResult.breakdown.byGender" class="breakdown-item">
                  <strong>性別：</strong>
                  <span v-for="(count, gender) in previewResult.breakdown.byGender" :key="gender" class="badge badge-info">
                    {{ gender === 'male' ? '男性' : gender === 'female' ? '女性' : gender }}: {{ count }}
                  </span>
                </div>
                <div v-if="previewResult.breakdown.byCity" class="breakdown-item">
                  <strong>城市：</strong>
                  <span v-for="(count, city) in previewResult.breakdown.byCity" :key="city" class="badge badge-info">
                    {{ city }}: {{ count }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

      </form>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { audienceRuleService } from '@/api/modules/audienceRules';
import { showSuccess, showError } from '@/utils/swal.js';
import MemberConditionGroups from '@/components/MemberConditionGroups.vue';
import {
  createEmptyGroup,
  describeConditionGroups,
  hydrateConditionGroups,
  legacyRulesToConditionGroups
} from '@/utils/memberConditions';

export default {
  name: 'AudienceRuleForm',
  components: {
    MemberConditionGroups
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const isEdit = computed(() => !!route.params.id);
    const submitting = ref(false);
    const previewLoading = ref(false);
    const previewResult = ref(null);
    const autoFillDescription = ref(true);
    const nameInput = ref(null);
    const conditionEditor = ref(null);
    const hydrating = ref(false);
    const conditionGroups = ref([createEmptyGroup()]);

    const formData = reactive({
      name: '',
      description: '',
      isActive: true,
      isDynamic: false,
      updateFrequency: null
    });

    const conditionSummary = ref('');

    const refreshSummary = () => {
      conditionSummary.value = conditionEditor.value?.describe?.()
        || describeConditionGroups(conditionGroups.value);
    };

    const applySummary = () => {
      refreshSummary();
      if (!autoFillDescription.value) return;
      if (conditionSummary.value) {
        formData.description = conditionSummary.value;
      }
    };

    watch(conditionGroups, () => {
      if (hydrating.value) return;
      applySummary();
    }, { deep: true });

    watch(autoFillDescription, (enabled) => {
      if (enabled) applySummary();
    });

    const handleDynamicChange = () => {
      if (!formData.isDynamic) {
        formData.updateFrequency = null;
      } else if (!formData.updateFrequency) {
        formData.updateFrequency = 'daily';
      }
    };

    const buildRulesConfig = () => ({
      conditionGroups: conditionGroups.value
    });

    const combinedLogic = () => (
      conditionGroups.value.length > 1
        ? 'OR'
        : (conditionGroups.value[0]?.logicalOperator || 'AND')
    );

    const ensureConditionsValid = async () => {
      const error = conditionEditor.value?.validate();
      if (error) {
        await showError('驗證失敗', error);
        return false;
      }
      return true;
    };

    const handlePreview = async () => {
      if (!(await ensureConditionsValid())) return;

      previewLoading.value = true;
      previewResult.value = null;
      try {
        const response = await audienceRuleService.previewAudience(buildRulesConfig());
        previewResult.value = response.data;
      } catch (error) {
        console.error('預覽受眾失敗:', error);
        await showError('預覽失敗', error.message || '預覽受眾失敗');
      } finally {
        previewLoading.value = false;
      }
    };

    const loadRule = async () => {
      if (!isEdit.value) return;
      hydrating.value = true;
      try {
        const response = await audienceRuleService.getAudienceRule(route.params.id);
        Object.assign(formData, {
          name: response.data.name,
          description: response.data.description,
          isActive: response.data.isActive,
          isDynamic: response.data.isDynamic,
          updateFrequency: response.data.updateFrequency
        });

        const config = response.data.rulesConfig;
        let groups;
        if (config?.conditionGroups?.length) {
          groups = config.conditionGroups;
        } else if (Array.isArray(config)) {
          groups = legacyRulesToConditionGroups({
            logic: response.data.logic || 'AND',
            rules: config
          });
        } else if (config?.rules) {
          groups = legacyRulesToConditionGroups(config);
        } else {
          groups = [createEmptyGroup()];
        }
        hydrateConditionGroups(groups);
        conditionGroups.value = groups;
      } catch (error) {
        console.error('載入規則詳情失敗:', error);
        await showError('載入失敗', error.message || '載入受眾規則詳情失敗');
        router.push('/client/audience-rules');
      } finally {
        hydrating.value = false;
        refreshSummary();
      }
    };

    const handleSubmit = async () => {
      if (!(await ensureConditionsValid())) return;
      submitting.value = true;
      try {
        const payload = {
          name: formData.name,
          description: formData.description,
          logic: combinedLogic(),
          rulesConfig: buildRulesConfig(),
          isActive: formData.isActive,
          isDynamic: formData.isDynamic
        };
        if (formData.isDynamic) {
          payload.updateFrequency = formData.updateFrequency || 'manual';
        }

        if (isEdit.value) {
          await audienceRuleService.updateAudienceRule(route.params.id, payload);
          await showSuccess('更新成功', '');
        } else {
          await audienceRuleService.createAudienceRule(payload);
          await showSuccess('新增成功', '');
        }
        router.push('/client/audience-rules');
      } catch (error) {
        console.error('儲存規則失敗:', error);
        await showError('儲存失敗', error.message || '儲存受眾規則失敗');
      } finally {
        submitting.value = false;
      }
    };

    const handleBack = () => {
      router.push('/client/audience-rules');
    };

    onMounted(async () => {
      if (isEdit.value) {
        await loadRule();
      }
      if (nameInput.value) {
        nameInput.value.focus();
      }
    });

    return {
      isEdit,
      formData,
      conditionGroups,
      conditionEditor,
      conditionSummary,
      submitting,
      previewLoading,
      previewResult,
      autoFillDescription,
      nameInput,
      handleDynamicChange,
      handlePreview,
      handleSubmit,
      handleBack
    };
  }
};
</script>

<style scoped>

.btn {
  padding: 6px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.btn-primary { background: #3b82f6; color: #fff; }
.btn-primary:hover:not(:disabled) { background: #2563eb; }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-secondary { background: #f3f4f6; color: #374151; border: 1px solid #e5e7eb; }
.btn-secondary:hover { background: #e5e7eb; }

/* 表單內聯選項 */
.form-group-inline-option {
  margin-top: 8px;
}

.form-group-inline-option .checkbox-label {
  font-size: 13px;
  color: #666;
}

.form-control:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
  opacity: 0.7;
}

/* 邏輯說明區塊（可折疊） */
.logic-info-box {
  margin-bottom: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 8px;
  color: white;
  overflow: hidden;
  transition: all 0.3s ease;
}

.logic-info-box.collapsed {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
  border: 2px solid #667eea;
}

.logic-info-box.collapsed .info-header {
  color: #667eea;
}

.info-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  user-select: none;
  transition: background 0.2s ease;
}

.info-header:hover {
  background: rgba(255, 255, 255, 0.1);
}

.logic-info-box.collapsed .info-header:hover {
  background: rgba(102, 126, 234, 0.05);
}

.info-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-header-left i {
  font-size: 20px;
}

.expand-icon {
  font-size: 20px;
  transition: transform 0.3s ease;
}

.info-title {
  font-size: 15px;
  font-weight: 600;
  margin: 0;
}

.info-content {
  padding: 0 16px 16px 16px;
}

.info-list {
  margin: 0 0 12px 0;
  padding-left: 20px;
}

.info-list li {
  margin-bottom: 8px;
  line-height: 1.6;
}

.info-list ul {
  margin-top: 4px;
  padding-left: 20px;
}

.info-list ul li {
  margin-bottom: 4px;
  font-size: 13px;
  opacity: 0.95;
}

.info-example {
  font-size: 13px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 4px;
  margin: 0;
  line-height: 1.8;
}

.result-text {
  display: block;
  margin-top: 8px;
  font-weight: 600;
}

/* 折疊動畫 */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s ease;
}

.slide-fade-enter-from {
  transform: translateY(-10px);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

.badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
}

.badge-and {
  background: rgba(103, 194, 58, 0.9);
  color: white;
}

.badge-or {
  background: rgba(245, 108, 108, 0.9);
  color: white;
}

/* 動態更新頻率說明樣式 */
.frequency-description {
  margin-top: 15px;
}

.description-box {
  display: flex;
  gap: 12px;
  padding: 15px;
  border-radius: 8px;
  margin-top: 10px;
  animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.description-box i {
  font-size: 24px;
  flex-shrink: 0;
  margin-top: 2px;
}

.description-content {
  flex: 1;
}

.description-content strong {
  display: block;
  font-size: 15px;
  margin-bottom: 8px;
  color: #333;
}

.description-content p {
  margin: 6px 0;
  font-size: 14px;
  line-height: 1.6;
  color: #666;
}

.description-content p:last-child {
  margin-bottom: 0;
}

/* 每日更新：綠色調 */
.description-daily {
  background-color: #f0fdf4;
  border: 1px solid #bbf7d0;
}

.description-daily i {
  color: #16a34a;
}

.description-daily .info-text {
  color: #16a34a;
  font-weight: 500;
}

/* 每週更新：紫色調 */
.description-weekly {
  background-color: #faf5ff;
  border: 1px solid #d8b4fe;
}

.description-weekly i {
  color: #7c3aed;
}

.description-weekly .info-text {
  color: #7c3aed;
  font-weight: 500;
}

/* 手動更新：灰色調 */
.description-manual {
  background-color: #f9fafb;
  border: 1px solid #d1d5db;
}

.description-manual i {
  color: #6b7280;
}

.description-manual .info-text {
  color: #6b7280;
  font-weight: 500;
}

.rule-params {
  margin-top: 15px;
  padding-left: 20px;
  border-left: 2px solid #eee;
}

.rule-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 20px;
  background-color: #fdfdfd;
}

.rule-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px dashed #e0e0e0;
}

.rule-header span {
  font-weight: 600;
  color: #555;
}

.rule-content {
  margin-top: 10px;
}

.preview-section {
  margin-top: 20px;
  padding: 15px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: #fdfdfd;
}

.preview-result {
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px dashed #e0e0e0;
}

.preview-result h4 {
  font-size: 16px;
  margin-bottom: 10px;
  color: #333;
}

.breakdown-item {
  margin-top: 10px;
  font-size: 14px;
}

.breakdown-item strong {
  margin-right: 8px;
}

.range-inputs,
.date-inputs {
  display: flex;
  align-items: center;
  gap: 10px;
}

.range-inputs span,
.date-inputs span {
  color: #666;
}

.checkbox-group {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
}

.location-checkboxes {
  max-height: 200px;
  overflow-y: auto;
  padding: 10px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: #fafafa;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
}

.radio-group {
  display: flex;
  gap: 20px;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
}

/* 條件效果說明區塊 */
.condition-summary-box {
  margin-top: 24px;
  padding: 16px;
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  border-radius: 8px;
  color: white;
}

.summary-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.summary-header i {
  font-size: 20px;
}

.summary-title {
  font-size: 15px;
  font-weight: 600;
}

.summary-content {
  background: rgba(255, 255, 255, 0.15);
  padding: 12px;
  border-radius: 4px;
}

.summary-text {
  margin: 0;
  line-height: 1.6;
  font-size: 14px;
}
</style>
